// Browser-side helpers for the TikTok and LinkedIn tags. They render in a
// Suspense hole of the site layout (SiteTracking, after advertising consent),
// so their snippets can run after a page's own tracking code. Each snippet
// announces itself with an event; these helpers wait for it, give up after a
// while, and send each event id at most once per page load.

export const TIKTOK_PIXEL_READY_EVENT = "guestgallery:tiktok-pixel-ready";
export const LINKEDIN_INSIGHT_READY_EVENT = "guestgallery:linkedin-insight-ready";

// LinkedIn Campaign Manager conversion id of the purchase conversion. Not set
// yet: until it is, the thank-you page's LinkedIn call carries no data, as the
// Insight Tag stub keeps only two arguments and queues
// lintrk("track", "conversion", {...}) as ["track", "conversion"]. With an id
// the call is LinkedIn's documented lintrk("track", { conversion_id, event_id }),
// which the stub keeps whole.
export const LINKEDIN_PURCHASE_CONVERSION_ID: number | null = null;

// How long a purchase event waits for its pixel after consent. The pixel
// normally runs within a second of hydration; past this the visit has moved on.
export const AD_PIXEL_READY_TIMEOUT_MS = 20_000;

interface AdPixelWindow {
  ttq?: {
    identify?: (data: Record<string, unknown>) => void;
    track?: (...args: unknown[]) => void;
  };
  lintrk?: (...args: unknown[]) => void;
}

function getAdPixelWindow(): AdPixelWindow {
  return window as unknown as AdPixelWindow;
}

export function isTikTokPixelReady(): boolean {
  const ttq = getAdPixelWindow().ttq;
  return typeof ttq?.track === "function" && typeof ttq.identify === "function";
}

export function isLinkedInInsightReady(): boolean {
  return typeof getAdPixelWindow().lintrk === "function";
}

/**
 * Runs `callback` once `isReady()` holds: now, or when `readyEvent` fires
 * within `timeoutMs`. Returns a cleanup that stops waiting.
 */
export function whenAdPixelReady(
  isReady: () => boolean,
  readyEvent: string,
  callback: () => void,
  timeoutMs: number = AD_PIXEL_READY_TIMEOUT_MS,
): () => void {
  if (isReady()) {
    callback();
    return () => {};
  }
  const stop = (): void => {
    window.removeEventListener(readyEvent, onReady);
    window.clearTimeout(timeout);
  };
  const onReady = (): void => {
    if (!isReady()) return;
    stop();
    callback();
  };
  const timeout = window.setTimeout(stop, timeoutMs);
  window.addEventListener(readyEvent, onReady);
  return stop;
}

const sentEventKeys = new Set<string>();

/** Runs `send` the first time `key` is seen in this page load. */
export function sendAdEventOnce(key: string, send: () => void): void {
  if (sentEventKeys.has(key)) return;
  sentEventKeys.add(key);
  try {
    send();
  } catch (error) {
    // A pixel failure must not break the page.
    console.warn("[tracking] ad pixel call failed", error);
  }
}

/** SHA-256 hex of the lowercased, trimmed email; null where Web Crypto is missing. */
export async function hashEmailForAds(email: string): Promise<string | null> {
  try {
    const data = new TextEncoder().encode(email.toLowerCase().trim());
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    return Array.from(new Uint8Array(hashBuffer))
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return null;
  }
}
